import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";
import { useEffect, useRef } from "react";
import { z } from "zod";
import { useMagicLinkLogin } from "@/api/mutations/auth-flows";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/api-client";

const magicLinkSearchSchema = z.object({
	chat: z.coerce.string().optional(),
});

export const Route = createFileRoute("/_public/login/mail/$token")({
	component: MagicLinkLoginPage,
	validateSearch: (search) => magicLinkSearchSchema.parse(search),
});

function MagicLinkLoginPage() {
	const { token } = Route.useParams();
	const { chat } = useSearch({ from: "/_public/login/mail/$token" });
	const navigate = useNavigate();
	const hasTriggered = useRef(false);

	const loginMutation = useMagicLinkLogin();

	// biome-ignore lint/correctness/useExhaustiveDependencies: One-time effect mit ref guard
	useEffect(() => {
		if (!hasTriggered.current) {
			hasTriggered.current = true;
			loginMutation.mutate(token, {
				onSuccess: (data) => {
					if (data.steps) {
						navigate({ to: "/register-steps" });
					} else if (chat) {
						navigate({ to: "/messenger/$id", params: { id: chat } });
					} else {
						navigate({ to: "/search" });
					}
				},
			});
		}
	}, []);

	if (loginMutation.isPending) {
		return (
			<div className="flex flex-col items-center gap-4 py-20">
				<Spinner size="lg" label="Login wird verarbeitet" />
				<p className="text-text-secondary text-base">Einen Moment bitte...</p>
			</div>
		);
	}

	if (loginMutation.isError) {
		const message =
			loginMutation.error instanceof ApiError
				? loginMutation.error.message
				: "Der Login-Link ist ungültig oder abgelaufen.";

		return (
			<div className="max-w-md mx-auto py-20">
				<Card padding="lg">
					<div className="flex flex-col items-center gap-4 text-center">
						<div className="flex h-16 w-16 items-center justify-center rounded-full bg-error/20">
							<AlertTriangle size={32} className="text-error" />
						</div>
						<h1 className="text-xl font-bold text-text">Login fehlgeschlagen</h1>
						<p className="text-base text-text-secondary">{message}</p>
						<Link to="/login">
							<Button variant="primary">Zum Login</Button>
						</Link>
					</div>
				</Card>
			</div>
		);
	}

	return null;
}
